var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getOrgChildrenIdsByParentIdFunctionDef, getOrganizationTreeFunctionDef, organizationEntityName, resolveOrgTreeItemIdsDef, } from '../../../../backend/features/organizations/shared';
import { replaceOrganizationTreeBranch } from '../../../../ui/features/organizations/utils';
import { useContactManagementTranslation } from '../../../../ui/hooks';
import { useTypedAnonymousPlatformFunctionCM } from '../../../../ui/hooks/useTypedAnonymousPlatformFunction';
import { useDataTreeUsingUrl, } from '@juvare/common-data-table-ui/DataTree';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { useTypedPlatformFunction, useTypedPlatformFunctionResponse, } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { getAppName } from '@juvare/platform-sdk-react/ui/utils';
import React from 'react';
export function useOrganizationSelectionTree({ useDataTreeFunction = useDataTreeUsingUrl, filteredOrganizationsIds, initialSelectedOrganizationIds, includeRootDirectory, disablePartialLoading, staticFilters, }) {
    var _a;
    const [parentId, setParentId] = React.useState();
    const expandedItems = React.useRef([]);
    const orgTree = React.useRef([]);
    const handleOnSelectAllAsync = useTypedPlatformFunction(getOrgChildrenIdsByParentIdFunctionDef);
    const { t } = useContactManagementTranslation();
    const resolvedAnonymousTreeItem = useTypedAnonymousPlatformFunctionCM(resolveOrgTreeItemIdsDef, getAppName(), organizationEntityName);
    const params = {
        useDataProvider: (request) => {
            const filters = Object.assign({}, request.filterData);
            const { search, permission } = filters;
            const isSearchValid = !!search && search.length >= 3;
            const filterData = isSearchValid ? { search } : { permission };
            const { data: organizationsTreeData, isLoading, error, mutate, } = useTypedPlatformFunctionResponse(getOrganizationTreeFunctionDef, {
                filterData: Object.keys(filterData).length > 0 ? filterData : undefined,
                staticFilters: Object.assign(Object.assign({}, staticFilters), { permission: (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member' ? 'member' : undefined }),
                initialOrganizationIds: filteredOrganizationsIds,
                maxDepth: 3,
                parentId: parentId ? parentId.split('_')[0] : undefined,
                parentPath: parentId ? parentId : undefined,
                maxExpandedIds: 500,
            });
            const data = React.useMemo(() => {
                var _a, _b;
                const highlightedIds = (organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.highlightedIds.length) && (organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.highlightedIds.length) > 0
                    ? organizationsTreeData.highlightedIds
                    : undefined;
                let organizations = includeRootDirectory && !parentId
                    ? [
                        {
                            id: 'root',
                            name: t('panels.organizations.root'),
                            children: (_a = organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.organizations) !== null && _a !== void 0 ? _a : [],
                            visible: false,
                            path: t('panels.organizations.root'),
                        },
                    ]
                    : ((_b = organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.organizations) !== null && _b !== void 0 ? _b : []);
                if (parentId) {
                    if (search) {
                        setParentId(undefined);
                    }
                    organizations = replaceOrganizationTreeBranch(orgTree.current, organizations, parentId);
                }
                orgTree.current = organizations;
                return {
                    items: organizations,
                    highlightedItemIds: highlightedIds,
                };
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.organizations, organizationsTreeData === null || organizationsTreeData === void 0 ? void 0 : organizationsTreeData.highlightedIds, filteredOrganizationsIds]);
            return {
                data,
                error: isSearchValid ? error : new Error(t('panels.organizations.searchValidation')),
                reload: () => mutate(),
                isLoading: isLoading,
            };
        },
    };
    const { dataTreeProps, filtersBarProps, appliedFilterData, reload } = useDataTreeFunction(Object.assign(Object.assign({}, params), { initialSelectedItemIds: initialSelectedOrganizationIds }));
    // DO NOT TRY TO COPY THIS APPROACH. This is not an ideal solution. This is done so that we don't have to load
    // preselected values in every other library that uses this hook,
    // requiring developers to know that they must load them in addition to passing them to this hook.
    // TODO remove after CUI-532
    const { onChangeSelectedItemIds } = dataTreeProps;
    React.useEffect(() => {
        function fetchResolvedAnonymousTreeItemIds() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!onChangeSelectedItemIds) {
                    return;
                }
                if (initialSelectedOrganizationIds === null || initialSelectedOrganizationIds === void 0 ? void 0 : initialSelectedOrganizationIds.length) {
                    const resolvedTreeItemIds = yield resolvedAnonymousTreeItem({
                        ids: initialSelectedOrganizationIds,
                    });
                    if (resolvedTreeItemIds) {
                        onChangeSelectedItemIds(() => resolvedTreeItemIds);
                    }
                }
                else {
                    onChangeSelectedItemIds(() => []);
                }
            });
        }
        fetchResolvedAnonymousTreeItemIds();
        // resolvedAnonymousTreeItem is a platform function and can not be included in dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSelectedOrganizationIds, onChangeSelectedItemIds]);
    const defaultDataTreeProps = Object.assign(Object.assign({}, dataTreeProps), { onSelectAllAsync: (parentId) => handleOnSelectAllAsync({
            parentId,
            includeByMembership: (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member' ? true : undefined,
            includeByPermissions: staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.orgPermissions,
            includeVisible: staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible,
        }).catch((e) => {
            handleErrorSnackbar(e);
            return [];
        }), onDeselectAllAsync: (parentId) => handleOnSelectAllAsync({
            parentId,
            includeByMembership: (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member' ? true : undefined,
            includeByPermissions: staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.orgPermissions,
            includeVisible: staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible,
        }).catch((e) => {
            handleErrorSnackbar(e);
            return [];
        }), onChangeExpandedItemIds: (action) => {
            const data = typeof action === 'function' ? action(undefined) : action;
            if (data && data.length > 0 && !disablePartialLoading) {
                const newExpandedItem = data[0];
                if (!expandedItems.current.includes(newExpandedItem) && newExpandedItem !== 'root') {
                    setParentId(newExpandedItem);
                    expandedItems.current.push(newExpandedItem);
                }
            }
            dataTreeProps.onChangeExpandedItemIds(action);
        }, 'data-test': 'organization-tree', loading: dataTreeProps.loading });
    const selectedOrganizationIds = React.useMemo(() => {
        var _a;
        return (_a = dataTreeProps.selectedItemIds) === null || _a === void 0 ? void 0 : _a.map((id) => id.split('_')[0]);
    }, [dataTreeProps.selectedItemIds]);
    return {
        dataTreeProps: defaultDataTreeProps,
        filtersBarProps,
        appliedFilterData,
        reload,
        activeOrganizationId: (_a = dataTreeProps.activeItemId) === null || _a === void 0 ? void 0 : _a.split('_')[0],
        selectedOrganizationIds,
        orgTree: orgTree.current,
    };
}
